import CloseIcon from '@/src/assets/svgs/CloseIcon'
import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import { VasAncillary } from '@/src/types/itinerary'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { benefitsIcons } from '.'
import SwipeableDrawerWithBackNavigation from '../../ui/swipeableDrawer'

interface BenefitsModalProps {
    isOpen: boolean
    onClose: () => void
    insurance: VasAncillary | null
    isSelected: boolean
    onInsuranceClick: () => void
}

const BenefitsModal = ({ isOpen, onClose, insurance, isSelected, onInsuranceClick }: BenefitsModalProps) => {
    const tCommon = useTranslations('common')
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    if (!insurance) return null

    const vasDetail = insurance.vasDetails?.[0]
    const {
        shortDescription = '',
        benefits = [],
        tripPremiumDetails: { totalPremiumAmount = 0 }
    } = vasDetail || {}

    const renderMobileContent = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
            <Box>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} sx={{ p: 2, pb: 0 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '16px', md: '18px' } }}>
                        {tCommon('allBenefits')}
                    </Typography>
                    <IconButton onClick={onClose} size={'small'}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ px: 2, py: 1, overflow: 'auto', maxHeight: '70dvh' }}>
                    <Stack gap={{ xs: 1.5, md: 2 }}>
                        {benefits.map((benefit, index) => {
                            const titleStr = benefit.title.split('||')
                            const title = titleStr[0]
                            const amount = titleStr[1]
                            const description = titleStr[2]
                            const icon = benefitsIcons[index]

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: { xs: 1, md: 1.5 }
                                    }}
                                >
                                    <Image src={icon} alt={benefit.title} width={28} height={28} />
                                    <Box>
                                        <Typography variant="body2" fontWeight={500} fontSize={'14px'}>
                                            {title} (
                                            {
                                                <Typography
                                                    component={'span'}
                                                    fontWeight={600}
                                                    fontSize={'inherit'}
                                                    color={'primary'}
                                                >
                                                    {amount}
                                                </Typography>
                                            }
                                            )
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight={500}
                                            color="textSecondary"
                                            fontSize={'13px'}
                                        >
                                            {description}
                                        </Typography>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Stack>
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                }}
            >
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '16px', md: '18px' } }}>
                    {formatCurrency(totalPremiumAmount)}
                </Typography>
                <Button
                    variant={isSelected ? 'contained' : 'outlined'}
                    // size="small"
                    onClick={() => {
                        onInsuranceClick()
                        onClose()
                    }}
                    sx={{
                        minWidth: { xs: '100px', md: '120px' },
                        fontSize: { xs: '14px', md: '16px' }
                    }}
                >
                    {isSelected ? tCommon('remove') : tCommon('add')}
                </Button>
            </Box>
        </Box>
    )

    //mobile swipeable drawer
    if (isMobile) {
        return (
            <SwipeableDrawerWithBackNavigation open={isOpen} onClose={onClose}>
                {renderMobileContent()}
            </SwipeableDrawerWithBackNavigation>
        )
    }
    //desktop popup
    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '14px', md: '16px' } }}>
                        {tCommon('allBenefits')}
                    </Typography>
                    <IconButton onClick={onClose} size={'medium'}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, md: 3 } }}>
                <Stack gap={{ xs: 1.5, md: 2 }}>
                    {benefits.map((benefit, index) => {
                        const titleStr = benefit.title.split('||')
                        const title = titleStr[0]
                        const amount = titleStr[1]
                        const description = titleStr[2]
                        const icon = benefitsIcons[index]

                        return (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: { xs: 1, md: 1.5 }
                                }}
                            >
                                <Image src={icon} alt={benefit.title} width={30} height={30} />
                                <Box>
                                    <Typography variant="body2" fontWeight={500} fontSize={'14px'}>
                                        {title} (
                                        {
                                            <Typography
                                                component={'span'}
                                                fontWeight={600}
                                                fontSize={'inherit'}
                                                color={'primary'}
                                            >
                                                {amount}
                                            </Typography>
                                        }
                                        )
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        fontWeight={500}
                                        color="textSecondary"
                                        fontSize={'13px'}
                                    >
                                        {description}
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    })}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, md: 3 }, py: { xs: 1.5, md: 2 } }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '14px', md: '16px' } }}>
                        {formatCurrency(totalPremiumAmount)}
                    </Typography>
                    <Button
                        variant={isSelected ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => {
                            onInsuranceClick()
                            onClose()
                        }}
                        sx={{
                            minWidth: { xs: '80px', md: '100px' },
                            fontSize: { xs: '14px', md: '16px' }
                        }}
                    >
                        {isSelected ? tCommon('remove') : tCommon('add')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default BenefitsModal
